/*
  File: src/pages/TvPage.jsx
  Purpose: TV content landing page with hero, recommended shows, and movies sections.
  Responsibilities:
  - Present curated lists using `TvShowGrid` and `MovieGrid` with filters UI stubs.
  - Provide quick navigation to streaming via hero CTA.
*/
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import TvHero from '../components/tv/TvHero';
import EventsScroller from '../components/home/EventsScroller';
import StreamingService from '../services/StreamingService';
import CategoryChips from '../components/home/CategoryChips';
import PosterGrid from '../components/home/PosterGrid';
import FeatureUnavailableModal from '../components/modals/FeatureUnavailableModal';
import StartStreamModal from '../components/Stream/StartStreamModal';

// Sample media items using assets from public/TV_shows
const base = process.env.PUBLIC_URL;
const rawTv = [
  { id: 'back', title: 'Back', image: 'back.jpg', genre: 'Action' },
  { id: 'breakaway', title: 'Breakaway', image: 'breakaway.jpg', genre: 'Action' },
  { id: 'dexter', title: 'Dexter', image: 'dexter.jpg', genre: 'Crime' },
  { id: 'home_team', title: 'Home Team', image: 'home_team.jpg', genre: 'Family' },
  { id: 'hostage', title: 'Hostage', image: 'hostage.jpg', genre: 'Thriller' },
  { id: 'law', title: 'Law', image: 'law.jpg', genre: 'Drama' },
  { id: 'mud', title: 'Mud', image: 'mud.jpg', genre: 'Thriller' },
  { id: 'president', title: 'President', image: 'president.jpg', genre: 'Drama' },
  { id: 'ruby', title: 'Ruby', image: 'ruby.jpg', genre: 'Crime' },
  { id: 'say_who_die', title: 'Say Who Die', image: 'say_who_die.jpg', genre: 'Action' },
  { id: 'scheda', title: 'Scheda', image: 'scheda.jpg', genre: 'Comedy' },
  { id: 'the_guest', title: 'The Guest', image: 'the_guest.jpg', genre: 'Drama' },
  { id: 'mystery', title: 'Mystery Show', image: '4c9b2ddde4be5e9ee4130ba6c23dd2c5.jpg', genre: 'Sci‑Fi' }
];

const rawMovies = [
  { id: 'president', title: 'President', image: 'president.jpg', genre: 'Drama' },
  { id: 'home_team', title: 'Home Team', image: 'home_team.jpg', genre: 'Family' },
  { id: 'mud', title: 'Mud', image: 'mud.jpg', genre: 'Thriller' },
  { id: 'say_who_die', title: 'Say Who Die', image: 'say_who_die.jpg', genre: 'Action' },
  { id: 'scheda', title: 'Scheda', image: 'scheda.jpg', genre: 'Comedy' },
  { id: 'back', title: 'Back', image: 'back.jpg', genre: 'Action' }
];

function TvPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const API_BASE = (process.env.REACT_APP_API_BASE || '/dev');
  const [series, setSeries] = useState([]);
  const [liveSeries, setLiveSeries] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState('');
  const [showFeatureModal, setShowFeatureModal] = useState(false);
  const [showNotActiveModal, setShowNotActiveModal] = useState(false);
  const [notActiveMessage, setNotActiveMessage] = useState('');
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);
  const streamingService = useMemo(() => new StreamingService(`${API_BASE}/streaming`), [API_BASE]);

  // Helper function to refresh series data
  const refreshSeriesData = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/series`, { headers: { accept: 'application/json' } });
      if (res.ok) {
        const data = await res.json();
        const items = Array.isArray(data?.items) ? data.items : [];
        setSeries(items);
        const liveSeries = items.find(s => String(s.status || '').toLowerCase() === 'live');
        setLiveSeries(liveSeries || null);
      }
    } catch (e) {
      console.warn('Failed to refresh series data:', e);
    }
  }, [API_BASE]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        // Fetch all series
        const res = await fetch(`${API_BASE}/series`, { headers: { accept: 'application/json' } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const items = Array.isArray(data?.items) ? data.items : [];
        if (!cancelled) {
          setSeries(items);
          // Find live series from the items array (only one can be live)
          const liveSeries = items.find(s => String(s.status || '').toLowerCase() === 'live');
          setLiveSeries(liveSeries || null);
        }
      } catch (e) {
        if (!cancelled) setError('Failed to load series');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [API_BASE]);

  // Handle scroll-to functionality when series/TV shows are loaded
  useEffect(() => {
    const scrollToId = searchParams.get('scrollTo');
    if (scrollToId && (series.length > 0 || rawTv.length > 0) && !loading) {
      // Small delay to ensure DOM is rendered
      const timeoutId = setTimeout(() => {
        const element = document.querySelector(`[data-item-id="${scrollToId}"]`);
        if (element) {
          element.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'nearest',
            inline: 'center'
          });
        }
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [series, loading, searchParams]);

  const seriesCards = useMemo(() => {
    return series.map(s => ({
      id: s.id,
      title: s.display_title || s.name,
      thumb: s.thumbnail_url,
      status: s.status || 'Not Started',
      video_s3_url: s.video_s3_url,
      is_active: s.is_active === true,
    }));
  }, [series]);

  const hasLiveSeries = useMemo(() => {
    return liveSeries !== null && liveSeries !== undefined;
  }, [liveSeries]);

  const handlePlaySeries = useCallback(async (item) => {
    // Check if series feature is enabled
    const isSeriesEnabled = process.env.REACT_APP_ENABLE_SERIES === 'true';
    if (!isSeriesEnabled) {
      setShowFeatureModal(true);
      return;
    }

    // Enforce active-only rule when configured
    const useActiveOnly = process.env.REACT_APP_USE_ACTIVE === 'true';
    if (useActiveOnly && item && item.is_active === false) {
      setNotActiveMessage('This series is not yet active.');
      setShowNotActiveModal(true);
      return;
    }

    try {
      setStartError('');
      setStarting(true);
      
      // Before starting, check if the IVS 'series' channel is already live with the SAME series
      // If it's live with a different series, we need to force restart
      try {
        const st = await streamingService.getStatus('series');
        const alreadyLive = (String(st?.status || '').toLowerCase() === 'live') || (st?.ivs_state === 'LIVE');
        if (alreadyLive && liveSeries && liveSeries.id === item?.id) {
          // Same series already live, just navigate
          navigate('/series/watch');
          return;
        }
        // If different series is live, we'll force restart below
      } catch (_) { /* ignore and attempt to start */ }
      
      // Update database to set this series as live (automatically stops other live series)
      if (item?.id) {
        try {
          const liveResp = await fetch(`${API_BASE}/series/live/start`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: item.id })
          });
          if (liveResp.ok) {
            const liveData = await liveResp.json();
            setLiveSeries(liveData.started_series);
            
            // Update the series list to reflect the status changes
            setSeries(prevSeries => 
              prevSeries.map(s => {
                if (s.id === item.id) {
                  return { ...s, status: 'live' };
                } else if (s.status === 'live') {
                  return { ...s, status: 'Not Started' };
                }
                return s;
              })
            );
            
            console.log('Series set to live:', liveData);
            
            // Also refresh from backend to ensure consistency
            setTimeout(() => refreshSeriesData(), 1000);
          }
        } catch (e) {
          console.warn('Failed to update series live status:', e);
        }
      }
      
      const src = item?.video_s3_url || '';
      let opts = { force: true, waitForLive: true, debug: true };
      if (typeof src === 'string' && src.startsWith('s3://')) {
        const without = src.replace('s3://', '');
        const slash = without.indexOf('/');
        if (slash > 0) {
          opts.bucket = without.slice(0, slash);
          opts.key = without.slice(slash + 1);
        } else {
          throw new Error('Invalid S3 URL');
        }
      } else if (typeof src === 'string' && (src.startsWith('http://') || src.startsWith('https://'))) {
        opts.sourceUrl = src;
      } else {
        throw new Error('This series has no video assigned yet');
      }
      
      // Ensure previous series stream is fully stopped before starting
      try { await streamingService.stopStream('series'); } catch (_) {}
      const waitUntilStopped = async (timeoutMs = 15000, pollMs = 800) => {
        const deadline = Date.now() + timeoutMs;
        while (Date.now() < deadline) {
          try {
            const st = await streamingService.getStatus('series');
            const s = String(st?.status || '').toLowerCase();
            const isLive = st?.ivs_state === 'LIVE' || s === 'live' || s === 'streaming' || st?.running === true;
            if (!isLive) return true;
          } catch (_) {}
          await new Promise(r => setTimeout(r, pollMs));
        }
        return false;
      };
      await waitUntilStopped(12000, 700);

      // Start streaming (backend will automatically stop other live channels)
      let startResp;
      try {
        startResp = await streamingService.startStream('series', opts);
      } catch (e) {
        console.warn('Series start error, retrying once after stop/wait:', e?.message || e);
        try { await streamingService.stopStream('series'); } catch (_) {}
        await waitUntilStopped(12000, 700);
        startResp = await streamingService.startStream('series', opts);
      }
      try {
        await streamingService.waitForLive(45000, 1500, 'series');
      } catch (_) { /* tolerate; Watch page will continue waiting */ }
      
      // Ensure we have a stream session id before navigating
      let sid = startResp?.stream_session_id || '';
      const deadline = Date.now() + 20000;
      while (!sid && Date.now() < deadline) {
        try {
          const st = await streamingService.getStatus('series');
          if (st?.stream_session_id) { sid = st.stream_session_id; break; }
        } catch (_) {}
        await new Promise(r => setTimeout(r, 800));
      }
      navigate('/series/watch', { state: { pendingLive: true, streamId: sid || undefined } });
    } catch (e) {
      setStartError(e?.message || 'Failed to start stream');
    } finally {
      setStarting(false);
    }
  }, [navigate, streamingService, API_BASE]);

  // Filters
  const tvCategories = ['All', 'Drama', 'Action', 'Crime', 'Thriller', 'Family', 'Comedy', 'Sci‑Fi'];
  const movieCategories = ['All', 'Action', 'Thriller', 'Comedy', 'Family', 'Drama'];
  const [tvFilter, setTvFilter] = useState('All');
  const [movieFilter, setMovieFilter] = useState('All');

  // Map raw items to PosterGrid format
  const tvItems = useMemo(() => rawTv.map(i => ({
    id: i.id,
    title: i.title,
    poster: `${base}/TV_shows/${i.image}`,
    tag: i.genre === 'Drama' ? 'Top 10' : undefined,
    meta: i.genre
  })), []);

  const movieItems = useMemo(() => rawMovies.map(i => ({
    id: i.id,
    title: i.title,
    poster: `${base}/TV_shows/${i.image}`,
    tag: i.genre === 'Action' ? 'Featured' : undefined,
    meta: i.genre
  })), []);

  const filteredTv = useMemo(() => tvItems.filter(i => tvFilter === 'All' || i.meta === tvFilter), [tvItems, tvFilter]);
  const filteredMovies = useMemo(() => movieItems.filter(i => movieFilter === 'All' || i.meta === movieFilter), [movieItems, movieFilter]);

  return (
    <div className="min-h-screen flex flex-col pt-14 sm:pt-16">
      <Navbar />

      <main className="flex-1">
        <TvHero 
          hasLiveSeries={hasLiveSeries}
          liveSeries={liveSeries}
          onWatch={async () => {
            const isSeriesEnabled = process.env.REACT_APP_ENABLE_SERIES === 'true';
            if (!isSeriesEnabled) {
              setShowFeatureModal(true);
              return;
            }
            
            if (hasLiveSeries) {
              // If there's a live series, check if IVS channel is still live
              try {
                const st = await streamingService.getStatus('series');
                const alreadyLive = (String(st?.status || '').toLowerCase() === 'live') || (st?.ivs_state === 'LIVE');
                if (alreadyLive) {
                  navigate('/series/watch');
                  return;
                }
              } catch (_) { /* ignore and navigate anyway */ }
            }
            
            navigate('/series/watch');
          }} 
        />

        {/* Series slider */}
        <section className="mt-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {startError && (
              <div className="mb-2 text-sm text-red-300">{startError}</div>
            )}
            <EventsScroller files={seriesCards} onSelect={handlePlaySeries} formatLabel={(c) => c.title} />
          </div>
        </section>

        {/* Recommended TV Shows */}
        <section className="mt-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold tracking-wide text-white/90">Recommended TV Shows</h2>
            </div>
            <div className="mt-3">
              <CategoryChips categories={tvCategories} selected={tvFilter} onChange={setTvFilter} />
            </div>
            <PosterGrid items={filteredTv} onSelect={() => {
              setShowComingSoonModal(true);
            }} />
          </div>
        </section>

        {/* Recommended Movies */}
        <section className="mt-8 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold tracking-wide text-white/90">Recommended Movies</h2>
            </div>
            <div className="mt-3">
              <CategoryChips categories={movieCategories} selected={movieFilter} onChange={setMovieFilter} />
            </div>
            <PosterGrid items={filteredMovies} onSelect={() => {
              setShowComingSoonModal(true);
            }} />
      </div>
        </section>
        <StartStreamModal isVisible={starting} isProcessing={starting} streamType="series" />
      </main>
      <Footer />

      {/* Feature Unavailable Modal */}
      {showFeatureModal && (
        <FeatureUnavailableModal 
          isOpen={showFeatureModal} 
          onClose={() => setShowFeatureModal(false)} 
        />
      )}

      {/* Not Active series modal */}
      {showNotActiveModal && (
        <FeatureUnavailableModal
          isOpen={showNotActiveModal}
          onClose={() => setShowNotActiveModal(false)}
          title="Series Not Active"
          message={notActiveMessage || 'This series is not yet active.'}
          primaryButtonText="OK"
        />
      )}

      {/* Coming soon modal for mock TV shows/movies */}
      {showComingSoonModal && (
        <FeatureUnavailableModal
          isOpen={showComingSoonModal}
          onClose={() => setShowComingSoonModal(false)}
          title="Coming soon"
          message={'This series or movie is not yet available on sceneshop. We\'ll let you know once it\'s available.'}
          primaryButtonText="OK"
        />
      )}
    </div>
  );
}

export default TvPage;

